import { useEffect, useRef, useState } from 'react';
const ChatbotOD = () => {
    // React state to track all messages (text only, for display)
    const [messages, setMessages] = useState([]);
    // State to track whether the conversation has started
    const [started, setStarted] = useState(false);
    // State to indicate the bot is responding (used for loading/thinking indicator)
    const [thinking, setThinking] = useState(true);
    // Ref to keep a persistent reference to the OpenDialog SDK instance
    const webchatRef = useRef(null);
    // Guard: only handle events while this instance is mounted (avoids duplicates when switching account)
    const isMountedRef = useRef(true);
    // Deduplicate bot messages: track last appended bot texts to avoid showing same message twice
    const lastAppendedBotTextsRef = useRef([]);
    const MAX_LAST_TEXTS = 20;
    // Effect runs once on mount: initializes the chatbot and sets up event listeners
    useEffect(() => {
        isMountedRef.current = true;
        // Block microphone permissions at browser level
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            navigator.mediaDevices.getUserMedia = () => {
                console.log('🎤 Microphone access blocked by application');
                return Promise.reject(new Error('Microphone access disabled'));
            };
        }
        // Clear ALL storage to ensure fresh start for new user
        sessionStorage.clear(); // Clear any existing session to start fresh (forces welcome messages)
        localStorage.removeItem('opendialog_conversation'); // Clear any OpenDialog conversation state
        // Clear chat history DOM so we never append to stale content (e.g. after account switch)
        const chatHistoryEl = document.getElementById('chat-history');
        if (chatHistoryEl) {
            chatHistoryEl.innerHTML = '';
        }
        lastAppendedBotTextsRef.current = [];
        console.log('🤖 ChatbotOD: Starting fresh conversation for new user');
        const containerId = 'chatbot-root';
        let container = document.getElementById(containerId);
        // If the DOM element for the SDK does not exist, create it
        if (!container) {
            container = document.createElement('div');
            container.id = containerId;
            container.style.display = 'none'; // Hide it from the layout
            document.body.appendChild(container);
        }
        // Create a new OpenDialog SDK instance and attach it to the placeholder container
        const webchat = new ODWebChat.OpenDialogChatSDK(`#${containerId}`);
        webchatRef.current = webchat; // Save instance to React ref
        // Settings passed to OpenDialog SDK to initialize a specific bot scenario
        const settings = {
            url: process.env.OPENDIALOG_URL,
            appKey: process.env.OPENDIALOG_APP_KEY,
            sdkEnabled: true, // Required to use the SDK (not iframe mode)
            iframe: false, // Ensure that we are not using the visual bubble version
            user: {
                custom: {
                    selected_scenario: process.env.OPENDIALOG_SCENARIO, // scenario ID for the chatbot
                    ms_token: localStorage.getItem('accessToken') // Pass access token to OpenDialog for authenticated webhook calls
                    // ? TODO : add ms_token? ms_continueSession: true,
                }
            },
        };
        // Initialize the chatbot with the above settings
        console.log('[ChatbotOD] Initializing with settings');
        webchat.init(settings);
        // ? TODO : add OpenDialog notification of token refresh
        // // Listen for token refresh events to update OpenDialog
        // const handleTokenRefresh = (event: CustomEvent) => {
        //   console.log('[ChatbotOD] Token refreshed, updating OpenDialog settings');
        //   const newToken = event.detail.newToken;
        //   // Update the settings with the new token
        //   const updatedSettings = {
        //     ...settings,
        //     user: {
        //       custom: {
        //         selected_scenario: process.env.OPENDIALOG_SCENARIO,
        //         ms_token: newToken
        //       }
        //     }
        //   };
        //   // Re-initialize with new token
        //   webchat.init(updatedSettings);
        // };
        // // Add event listener for token refresh
        // window.addEventListener('tokenRefreshed', handleTokenRefresh as EventListener);
        // Listen to bot messages using the event name 'messageReceived'
        // Reference: https://docs.opendialog.ai/developing-with-opendialog/webchat/sdk/events#messagereceived
        webchat.on('messageReceived', (e) => {
            if (!isMountedRef.current)
                return; // Avoid appending after unmount (e.g. old listener when switching account)
            console.log('[ChatbotOD] Message Received:', e.detail);
            setStarted(true); // Mark the conversation as started
            const arr = Array.isArray(e.detail.messages) ? e.detail.messages : [e.detail.messages];
            // Remove typing bubble if present
            const oldTyping = document.querySelector('#chat-history .typing');
            if (oldTyping)
                oldTyping.remove();
            // For each message received from the bot, append it to the DOM manually (with deduplication)
            arr.forEach((msg) => {
                var _a, _b, _c;
                if (msg.type === 'button' && ((_a = msg.data) === null || _a === void 0 ? void 0 : _a.buttons)) {
                    // Calls a function to render button
                    // renderExternalButton(msg.data, webchatRef);
                    // setTimeout(moveNativeButtonToCustomPanel, 500); // slight delay to ensure rendering
                }
                if ((_b = msg.data) === null || _b === void 0 ? void 0 : _b.text) {
                    const text = msg.data.text;
                    const lastTexts = lastAppendedBotTextsRef.current;
                    if (lastTexts.includes(text))
                        return; // Deduplicate: skip if we already appended this exact text
                    lastTexts.push(text);
                    if (lastTexts.length > MAX_LAST_TEXTS)
                        lastTexts.shift();
                    const customDisplay = document.getElementById('chat-history');
                    if (customDisplay && isMountedRef.current) {
                        const p = document.createElement('p');
                        p.innerHTML = `<span class="bot"></span>${text}`;
                        customDisplay.appendChild(p);
                        customDisplay.scrollTop = customDisplay.scrollHeight;
                    }
                }
                // Handle OpenDialog meta-message (type = 'meta', data.elements = { key: value })
                if (msg.type === 'meta' && ((_c = msg.data) === null || _c === void 0 ? void 0 : _c.elements)) {
                    console.log('[ChatbotOD] Meta received:', msg.data.elements);
                    handleAttributes(msg.data.elements); // 
                }
                // Re-enable input & send button
                const inputField = document.getElementById('chat-input');
                const sendButton = document.getElementById('chat-send');
                if (inputField)
                    inputField.disabled = false;
                if (sendButton)
                    sendButton.disabled = false;
                setThinking(false); // React state sync
            });
        });
        // Event: when the user sends a message (used here to show 'thinking' feedback)
        webchat.on('messageSent', (e) => {
            if (!isMountedRef.current)
                return;
            console.log('[ChatbotOD] messageSent event:', e.detail);
            setThinking(true); // Bot is now processing
            // Disable input & send button
            const inputField = document.getElementById('chat-input');
            const sendButton = document.getElementById('chat-send');
            if (inputField)
                inputField.disabled = true;
            if (sendButton)
                sendButton.disabled = true;
            // Wait until user's message is inserted in DOM before adding typing
            setTimeout(() => {
                if (!isMountedRef.current)
                    return;
                // Add animated typing bubble
                const customDisplay = document.getElementById('chat-history');
                if (customDisplay) {
                    // Remove existing typing bubble if any
                    const oldTyping = customDisplay.querySelector('.typing');
                    if (oldTyping)
                        oldTyping.remove();
                    // Create new typing element
                    const typing = document.createElement('p');
                    typing.className = 'typing';
                    customDisplay.appendChild(typing);
                    customDisplay.scrollTop = customDisplay.scrollHeight;
                }
            }, 50); // Small delay ensures user message is added first
        });
        // Event: when the bot is loaded and ready
        webchat.on('loaded', () => {
            setTimeout(() => {
                if (!isMountedRef.current)
                    return;
                setThinking(false);
                const inputField = document.getElementById('chat-input');
                const sendButton = document.getElementById('chat-send');
                const indicator = document.getElementById('thinking-indicator');
                if (inputField)
                    inputField.disabled = false;
                if (sendButton)
                    sendButton.disabled = false;
                if (indicator)
                    indicator.style.display = 'none';
            }, 500); // remove thinking state
        });
        // Cleanup function to destroy chatbot instance on unmount
        return () => {
            isMountedRef.current = false; // First: stop handling any further events (avoids duplicates on account switch)
            console.log('🤖 ChatbotOD: Cleaning up chatbot instance');
            if (webchatRef.current) {
                try {
                    // Try to restart/clear the conversation
                    webchatRef.current.restart(true); // true = clear user state
                }
                catch (error) {
                    console.warn('Error restarting chatbot:', error);
                }
                webchatRef.current = null;
            }
        };
    }, []); // Only run once on mount
    // Effect to connect the input and button in HTML with this component logic
    useEffect(() => {
        const sendButton = document.getElementById('chat-send'); // The send button from HTML
        const inputField = document.getElementById('chat-input'); // The text input field
        // Function to handle click or Enter key event for sending a message
        const handleClick = () => {
            const text = inputField === null || inputField === void 0 ? void 0 : inputField.value.trim();
            if (!text || !webchatRef.current)
                return;
            // Send message via OpenDialog SDK
            webchatRef.current.sendMessage({ text });
            // Also display it manually in the DOM (UI is HTML-managed, not React)
            const customDisplay = document.getElementById('chat-history');
            if (customDisplay) {
                const p = document.createElement('p');
                p.innerHTML = `<span class="user"></span>${text}`;
                customDisplay.appendChild(p);
                customDisplay.scrollTop = customDisplay.scrollHeight;
            }
            inputField.value = ''; // Reset the input
        };
        // ! TODO: REMOVE AFTER TEST ANTOINE !
        // Test button for Antoine to test intents
        const testButton = document.getElementById('antoine-test-btn');
        const testInput = document.getElementById('antoine-test-input');
        const handleTestClick = () => {
            if (!webchatRef.current) {
                console.warn('Chatbot not ready');
                return;
            }
            // ! TODO: ANTOINE - Change this intent name to test different intents !
            const intentName = 'welcome.intent'; // ! ← ANTOINE: Change this to test other intents
            // Get custom text from input (optional)
            const customText = (testInput === null || testInput === void 0 ? void 0 : testInput.value.trim()) || `Testing intent: ${intentName}`;
            console.log('Testing intent:', intentName, 'with text:', customText);
            // Send button_response message as specified by OpenDialog
            const payload = {
                text: customText,
                type: 'button_response',
                callback: intentName,
                data: {
                    text: customText,
                    value: `test_attribute.${intentName}`
                }
            };
            console.log('Sending payload:', payload);
            webchatRef.current.sendMessage(payload);
            // Clear input
            testInput.value = '';
        };
        // ! END TEST ANTOINE !
        // Attach event listeners
        sendButton === null || sendButton === void 0 ? void 0 : sendButton.addEventListener('click', handleClick);
        inputField === null || inputField === void 0 ? void 0 : inputField.addEventListener('keydown', (e) => {
            if (e.key === 'Enter')
                handleClick();
        });
        // ! TODO: REMOVE AFTER TEST ANTOINE !
        testButton === null || testButton === void 0 ? void 0 : testButton.addEventListener('click', handleTestClick);
        // ! END TEST ANTOINE !
        // Clean up the listeners on component unmount
        return () => {
            sendButton === null || sendButton === void 0 ? void 0 : sendButton.removeEventListener('click', handleClick);
            // ! TODO: REMOVE AFTER TEST ANTOINE !
            testButton === null || testButton === void 0 ? void 0 : testButton.removeEventListener('click', handleTestClick);
            // ! END TEST ANTOINE !
        };
    }, []);
    // This component doesn't render JSX - it only connects logic and side effects
    return null;
};
export default ChatbotOD;
/**
 * Centralized attribute handler.
 */
const handleAttributes = (attributes) => {
    var _a;
    // Check if the load of a new PDB file is requested
    if (attributes.receptorPreparationOK_test && attributes.pdbFileUrl_b) { // If the receptor preparation is OK and the PDB file URL is present
        console.log('✅ Loading PDB from:', attributes.pdbFileUrl_b, attributes.pdbId_b);
        (_a = window.loadPdbInMolstar) === null || _a === void 0 ? void 0 : _a.call(window, attributes.pdbId_b, attributes.pdbFileUrl_b);
    }
};
const moveNativeButtonToCustomPanel = () => {
    // Locate the custom panel and the wrapper containing the OpenDialog button
    const panel = document.getElementById('info-panel');
    const wrapper = document.querySelector('.od-body-form');
    // If either the panel or wrapper is not found, log a warning and exit
    if (!panel || !wrapper) {
        console.warn('⚠️ Panel or wrapper not found.');
        return;
    }
    // Check if the wrapper is already inside the panel
    const alreadyInPanel = panel.contains(wrapper);
    // If the wrapper is not already in the panel, append it
    if (!alreadyInPanel) {
        panel.appendChild(wrapper);
    }
    // Initially hide the wrapper to avoid a flash before it's ready
    wrapper.style.display = 'none';
    // Create a MutationObserver to monitor changes in the wrapper's content
    const observer = new MutationObserver(() => {
        // Look for the new button inside the wrapper
        const newBtn = wrapper.querySelector('.odl-button');
        // If a button is found and it hasn't been bound yet
        if (newBtn && !newBtn.dataset.bound) {
            console.log('[ChatbotOD] ✅ Button injected or updated, binding click');
            // Mark the button as bound to avoid duplicate bindings
            newBtn.dataset.bound = 'true';
            // Show the wrapper now that the button is ready
            wrapper.style.display = 'block';
            // Add a click event listener to the button
            newBtn.addEventListener('click', () => {
                console.log('[ChatbotOD] 🚫 Button clicked. Hiding wrapper.');
                // Hide the wrapper when the button is clicked
                wrapper.style.display = 'none';
                // Remove the fallback input form from OpenDialog (unnecessary input field)
                const fallbackForm = document.querySelector('form.od-user-input');
                if (fallbackForm)
                    fallbackForm.remove();
            });
            // Stop observing once the button is ready and bound
            observer.disconnect();
        }
    });
    // Start observing the wrapper for changes in its child elements
    observer.observe(wrapper, {
        childList: true,
        subtree: true,
    });
};
/**
 * Dynamically renders the first chatbot button into the custom UI panel.
 * This function assumes that the chatbot sends a 'button' type message containing a buttons array.
 *
 * @param data - The MessageData object containing button details.
 */
const renderExternalButton = (data, webchatRef) => {
    var _a;
    console.log("button", data);
    // Locate the target panel where the button should appear (custom UI area)
    const panel = document.getElementById('info-panel');
    if (!panel) {
        console.warn('⚠️ Info panel not found. Cannot render chatbot button.');
        return;
    }
    // Retrieve the first button from the array (current use case handles single button)
    const btnData = (_a = data.buttons) === null || _a === void 0 ? void 0 : _a[0];
    if (!btnData) {
        console.warn('⚠️ No button data found in message.');
        return;
    }
    // Create a new HTML button element
    const button = document.createElement('button');
    button.textContent = btnData.text || 'Action'; // Fallback label if text is missing
    button.className = 'chatbot-action-btn'; // Apply custom styling class
    // Define what happens when the user clicks the button
    button.onclick = () => {
        var _a;
        // Construct the payload to simulate a native button click
        const payload = btnData.callback_id // Check if callback_id is present
            ? {
                type: "button_response",
                text: btnData.text,
                callback: btnData.callback_id,
            }
            : {
                type: "text",
                text: btnData.text // Fallback to button text if no callback_id
            };
        console.log('[CustomButton] Submitting payload:', payload);
        (_a = webchatRef.current) === null || _a === void 0 ? void 0 : _a.sendMessage(payload);
        button.remove();
    };
    // Append the button to the panel so it becomes visible to the user
    panel.appendChild(button);
};
